import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Collection } from '@beyond-js/ui';
import type { Capture, Showing } from '@snapshots/domain';
import { Address, useFrame } from '@snapshots/navigation';
import { usePresence } from '@snapshots/session';
import type { Refusal } from '@snapshots/transport';
import { Outage, Problems, type Missed } from '@snapshots/ui';
import { Entries } from './entries';

/** What the page learns from the list's reads: a scope it cannot read, a first answer with nothing, a read nobody answered */
export interface Heard {
	missing?: Refusal;
	nothing: boolean;
	outage?: Missed;
}

/** The states a person filters the list by, as the service names them; all but deleted when none is chosen */
const states = ['active', 'pending', 'failed', 'cancelled', 'deleted'] as const;

/**
 * The snapshots list as the family's list (D162, Conduict's All conversations): `@beyond-js/ui`'s `Collection`
 * with `presentation: 'list'`, a page of 20 at a time read from the service, which searches (title, identity,
 * package, the start of a digest) and filters by state itself (L8), so nothing is capped and the end line says
 * where the list ends (L5). Its search and its state filter are the shared tools, placed in the page header's
 * action region (`outlet`) before its own action and drawn from the first frame (D194); the address keeps them.
 * Rows, groups and the end line are `Entries`'.
 *
 * A first answer with nothing in it, unsearched and unfiltered, is the page's first run (`nothing`), which the
 * page draws in the list's place under the tools already drawn (D185). A read nobody answered joins the page's
 * one line (D163) through the collection's notice, and the list says "Not available right now" in its place.
 * A scope the person cannot read is `missing`. A read the ended session kept from being sent waits for the
 * renewal, keeping what the list shows.
 */
export function SnapshotList({ address, outlet, hidden, onHeard }: { address: Address; outlet: HTMLElement | null; hidden: boolean; onHeard: (heard: Partial<Heard>) => void }) {
	const presence = usePresence();
	const frame = useFrame();
	const navigate = useNavigate();
	const [, setParameters] = useSearchParams();
	const { pathname, search } = useLocation();
	const host = useRef<HTMLDivElement>(null);
	const list = useRef<Collection<Capture>>();
	// The callbacks of a list made once read what is current
	const latest = useRef({ address, presence, frame, navigate, setParameters, from: `${pathname}${search}`, onHeard });
	latest.current = { address, presence, frame, navigate, setParameters, from: `${pathname}${search}`, onHeard };
	const { text, language } = presence;

	useEffect(() => {
		const { project, organization } = latest.current.address;
		const words = latest.current.presence;
		const entries = new Entries({ ...words, names: latest.current.frame.names }, { project, organization });
		const open = (id: string) => latest.current.navigate(`/snapshots/${encodeURIComponent(id)}`, { state: { from: latest.current.from } });
		let failed: Refusal | undefined;
		const collection = new Collection<Capture>({
			label: text('captures.title'),
			presentation: 'list',
			framed: true,
			columns: [{ key: 'title', label: text('captures.column.title'), primary: true, value: row => entries.title(row) }],
			row: row => entries.row(row),
			group: row => entries.group(row),
			end: count => entries.end(count),
			key: row => row.id,
			link: row => `/snapshots/${encodeURIComponent(row.id)}`,
			onopen: row => open(row.id),
			search: true,
			filters: [{ name: 'state', label: text('captures.filter.state'), all: text('captures.filter.current'), options: states.map(value => ({ value, label: text(`captures.state.${value}`) })) }],
			tools: 'external',
			limit: 20,
			state: SnapshotList.state(latest.current.address),
			labels: { ...Collection.labels[language], search: text('list.search'), matches: ({ query }: { query?: unknown }) => (query ? text('list.matches', { query: String(query) }) : text('list.filtered')) },
			empty: { title: text('captures.empty.title') },
			onstate: next => {
				// A pasted snapshot address opens that snapshot instead of searching for its words (CNT-85)
				const pasted = Address.capture(next.query);
				if (pasted) return open(pasted);
				const current = latest.current.address;
				const view = { query: next.query, page: next.page };
				latest.current.setParameters(current.with(view, (next.filters.state || undefined) as Showing | undefined), { replace: current.typed(view) });
			},
			source: async ({ query, filters, page, limit }) => {
				const answer = await SnapshotList.read(latest.current, { project, organization, text: query, state: (filters.state || undefined) as Showing | undefined, page, limit }).catch((cause: unknown) => {
					failed = Problems.refusal(cause);
					if (failed.code === 'NOT_FOUND') latest.current.onHeard({ missing: failed, outage: undefined });
					throw cause;
				});
				const nothing = page === 1 && !query && !filters.state && !answer.captures.length;
				// Said at once, before the list draws its own empty state, which the first run replaces
				if (host.current) host.current.toggleAttribute('data-first', nothing);
				latest.current.onHeard({ nothing, missing: undefined });
				entries.prepare(answer.captures);
				return { rows: answer.captures, total: answer.total };
			},
			explain: cause => Problems.say(latest.current.presence.text, Problems.refusal(cause), 'read', { size: latest.current.presence.size }),
			// A read nobody answered joins the page's one line, which reads it again (D163)
			outage: cause => Boolean(Outage.cause(Problems.refusal(cause))),
			recovery: {
				set: (_key: unknown, entry: { run?: () => unknown }) => {
					if (failed) latest.current.onHeard({ outage: { key: 'list', title: latest.current.presence.text('outage.read.list'), refusal: failed, run: async () => entry.run?.() } });
				},
				delete: () => latest.current.onHeard({ outage: undefined })
			} as never
		});
		list.current = collection;
		host.current?.append(collection.element);
		return () => {
			list.current = undefined;
			collection.destroy();
			collection.element.remove();
			entries.destroy();
		};
		// One list per scope and language: the page keys this component by its scope
	}, [text, language]);

	// The address restores the search, the filter and the page on back and forward
	const wanted = SnapshotList.state(address);
	const key = JSON.stringify(wanted);
	useEffect(() => {
		const current = list.current;
		if (current && JSON.stringify(current.state) !== key) current.state = wanted;
	}, [key]);

	// The shared tools stand in the page header's action region, before its action (L8)
	useLayoutEffect(() => {
		const tools = list.current?.tools.element;
		if (!outlet || !tools) return;
		outlet.append(tools);
		return () => tools.remove();
	}, [outlet]);

	return <div ref={host} data-list={address.scope} hidden={hidden} />;
}

SnapshotList.state = (address: Address): { query: string; filters: Record<string, string>; page: number } => ({ query: address.view.query, filters: address.state ? { state: address.state } : {}, page: address.view.page });

/** One page of the list, waiting through a renewal for a read the ended session kept from being sent; names learned as rows arrive */
SnapshotList.read = async (
	current: { presence: ReturnType<typeof usePresence>; frame: ReturnType<typeof useFrame> },
	query: { project?: string; organization?: string; text: string; state?: Showing; page: number; limit: number }
) => {
	const { captures, family } = current.presence;
	const { names, resumed } = current.frame;
	for (;;) {
		try {
			const answer = await captures.find(query);
			for (const capture of answer.captures) {
				if (!names.name(capture.project) && names.wants(capture.organization)) void family.describe({ organization: capture.organization }).then(found => names.learn(found));
			}
			return answer;
		} catch (cause) {
			if (!Problems.refusal(cause).quiet) throw cause;
			await resumed();
		}
	}
};
